import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icons from './Icons';

const HeroSection = ({ isDark }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    location: '',
    pickupDate: '',
    pickupTime: '',
    returnDate: ''
  });

  const heroSlides = [
    {
      image: 'https://www.financialexpress.com/auto/images/thar-lx-hard-top-diesel-at-4wd-full-details-1724230881880.jpg',
      title: 'Luxury Cars',
      subtitle: 'Experience Premium Comfort'
    },
    {
      image: 'https://gtaindiamods.com/cdn/shop/files/DesktopScreenshot2022.07.01-11.09.59.70-picsay_1920x.png?v=1756771155',
      title: 'SUVs & MUVs',
      subtitle: 'Perfect for Family Trips'
    },
    {
      image: '/logo.jpg',
      title: 'Wedding Cars',
      subtitle: 'Make Your Day Special'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const locations = [
    { value: 'udaipur-airport', label: 'Udaipur Airport', icon: '✈️' },
    { value: 'railway-station', label: 'Railway Station', icon: '🚂' },
    { value: 'bus-stand', label: 'Bus Stand', icon: '🚌' },
    { value: 'city-center', label: 'City Center', icon: '🏙️' },
    { value: 'hotel-pickup', label: 'Hotel Pickup', icon: '🏨' },
    { value: 'other', label: 'Other Location', icon: '📍' }
  ];

  const handleSearchCars = (e) => {
    e.preventDefault();
    
    // Scroll to the Luxury Cars section (premium-collection)
    const luxuryCarsSection = document.getElementById('premium-collection');
    if (luxuryCarsSection) {
      luxuryCarsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // If not on homepage, navigate to homepage with hash
      navigate('/#premium-collection');
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Slides */}
      {heroSlides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            currentSlide === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className={`w-full h-full object-cover sm:object-cover ${
              slide.image === '/logo.jpg' ? 'object-contain sm:object-cover' : ''
            }`}
          />
        </div>
      ))}
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30"></div>
      
      {/* Animated Particles - Hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#c9a962]/40 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      {/* Floating Car Icons - Hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        <div className="absolute top-20 right-20 text-6xl opacity-10 animate-float" style={{ animationDelay: '0s' }}>🚗</div>
        <div className="absolute bottom-40 left-20 text-8xl opacity-10 animate-float" style={{ animationDelay: '2s' }}>🚙</div>
        <div className="absolute top-40 left-1/4 text-4xl opacity-10 animate-float" style={{ animationDelay: '4s' }}>🚘</div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 text-center pt-16 sm:pt-20 md:pt-24 pb-4 sm:pb-8">
        {/* Badge */}
       

        {/* Main Heading */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 sm:mb-6 text-white animate-fade-in-up leading-tight px-2">
          Every Drive Begins
          <br />
          <span className="relative inline-block">
            <span className="text-[#c9a962]">with Brothers.</span>
            <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
              <path d="M2 10C50 4 150 2 298 10" stroke="#c9a962" strokeWidth="3" strokeLinecap="round" className="animate-draw" />
            </svg>
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-10 max-w-3xl mx-auto animate-fade-in-up leading-relaxed px-4" style={{ animationDelay: '0.2s' }}>
          Experience luxury on wheels with our premium fleet. Choose from the finest 
          <span className="text-[#c9a962] font-semibold"> luxury cars</span> for your unforgettable journey in the 
          <span className="text-[#c9a962] font-semibold"> City of Lakes.</span>
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center mb-6 sm:mb-12 animate-fade-in-up px-2" style={{ animationDelay: '0.4s' }}>
          <a 
            href="/luxury-cars" 
            className="group relative px-4 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-bold uppercase tracking-wider rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-[#c9a962]/40 hover:scale-105 text-xs sm:text-sm md:text-base"
          >
            <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Browse Cars
            </span>
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </a>
          
          <a 
            href="tel:9602360369" 
            className="group relative px-4 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-transparent border-2 border-white text-white font-bold uppercase tracking-wider rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 hover:border-[#c9a962] hover:shadow-2xl hover:shadow-[#c9a962]/20 hover:scale-105 text-xs sm:text-sm md:text-base"
          >
            <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3 group-hover:text-black transition-colors">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call Now
            </span>
            <div className="absolute inset-0 bg-[#c9a962] translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </a>

          <a 
            href="https://wa.me/919602360369"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-4 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 bg-[#25D366] text-white font-bold uppercase tracking-wider rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-[#25D366]/40 hover:scale-105 text-xs sm:text-sm md:text-base"
          >
            <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </span>
          </a>
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 md:gap-6 mb-6 sm:mb-8 animate-fade-in-up px-2" style={{ animationDelay: '0.6s' }}>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm">
            <span className="text-[#c9a962] text-sm sm:text-base">⭐</span>
            <span className="text-white text-[10px] sm:text-sm font-medium">4.9/5 Rating</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm">
            <span className="text-[#c9a962] text-sm sm:text-base">🚗</span>
            <span className="text-white text-[10px] sm:text-sm font-medium">50+ Cars</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm">
            <span className="text-[#c9a962] text-sm sm:text-base">😊</span>
            <span className="text-white text-[10px] sm:text-sm font-medium">5000+ Customers</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
            <span className="text-[#c9a962]">✓</span>
            <span className="text-white text-sm font-medium">100% Insured</span>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-2 sm:gap-3 mb-4 sm:mb-8">
          {heroSlides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`group relative transition-all duration-300 ${
                currentSlide === index ? 'w-8 sm:w-12' : 'w-2.5 sm:w-3'
              } h-2.5 sm:h-3 rounded-full overflow-hidden`}
            >
              <span className={`absolute inset-0 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'bg-[#c9a962]' : 'bg-white/30 group-hover:bg-white/50'
              }`}></span>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Booking Form */}
      <div className="relative z-20 px-3 sm:px-4 pb-4 sm:pb-8">
        <div className="max-w-6xl mx-auto">
          {/* Form Header - Click to scroll to cars section */}
          <div className="text-center mb-3 sm:mb-6">
            <button
              type="button"
              onClick={handleSearchCars}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#c9a962]/20 border border-[#c9a962]/30 hover:bg-[#c9a962]/30 transition-colors cursor-pointer"
            >
              <span className="text-base sm:text-xl">🚀</span>
              <span className="text-[#c9a962] text-xs sm:text-sm font-bold uppercase tracking-wider">Quick Booking</span>
            </button>
          </div>
          </div>
          </div>

          {/* Form Card */}
        {/* <div className={`relative p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl overflow-hidden ${
            isDark 
              ? 'bg-[#1a1a1a]/95 backdrop-blur-xl border border-gray-800' 
              : 'bg-white/95 backdrop-blur-xl shadow-2xl border border-gray-100'
          }`}> */}
            {/* Decorative Elements - Hidden on mobile */}
            {/* <div className="absolute top-0 right-0 w-64 h-64 bg-[#c9a962]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 hidden md:block"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#c9a962]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 hidden md:block"></div>

            <form className="relative z-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6"> */}
              {/* Location Dropdown - Select pick-up location for car rental */}
                {/* <div className="sm:col-span-2 lg:col-span-1">
                  <label className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold mb-2 sm:mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="text-[#c9a962]">📍</span>
                    Pick-up Location
                  </label>
                  <div className="relative">
                    <select 
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl appearance-none cursor-pointer transition-all duration-300 text-sm ${
                        isDark 
                          ? 'bg-[#0d0d0d] text-white border-2 border-gray-700 focus:border-[#c9a962]' 
                          : 'bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-[#c9a962]'
                      } hover:border-[#c9a962]`}
                    >
                      <option value="">Select Location</option>
                      {locations.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.icon} {loc.label}</option>
                      ))}
                    </select>
                    <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div> */}

                {/* Pick-up Date - Select the date when you want to pick up the car */}
                {/* <div>
                  <label className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold mb-2 sm:mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="text-[#c9a962]">📅</span>
                    Pick-up Date
                  </label>
                  <input 
                    type="date" 
                    value={formData.pickupDate}
                    onChange={(e) => setFormData({...formData, pickupDate: e.target.value})}
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-300 text-sm ${
                      isDark 
                        ? 'bg-[#0d0d0d] text-white border-2 border-gray-700 focus:border-[#c9a962]' 
                        : 'bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-[#c9a962]'
                    } hover:border-[#c9a962]`}
                  />
                </div> */}

                {/* Pick-up Time - Select the time when you want to pick up the car */}
                {/* <div>
                  <label className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold mb-2 sm:mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="text-[#c9a962]">🕐</span>
                    Pick-up Time
                  </label>
                  <div className="relative">
                    <select 
                      value={formData.pickupTime}
                      onChange={(e) => setFormData({...formData, pickupTime: e.target.value})}
                      className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl appearance-none cursor-pointer transition-all duration-300 text-sm ${
                        isDark 
                          ? 'bg-[#0d0d0d] text-white border-2 border-gray-700 focus:border-[#c9a962]' 
                          : 'bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-[#c9a962]'
                      } hover:border-[#c9a962]`}
                    >
                      <option value="">Select Time</option>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div> */}

                {/* Return Date - Select the date when you will return the car */}
                {/* <div>
                  <label className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold mb-2 sm:mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="text-[#c9a962]">📅</span>
                    Return Date
                  </label>
                  <input 
                    type="date" 
                    value={formData.returnDate}
                    onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                    className={`w-full px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-300 text-sm ${
                      isDark 
                        ? 'bg-[#0d0d0d] text-white border-2 border-gray-700 focus:border-[#c9a962]' 
                        : 'bg-gray-50 text-gray-900 border-2 border-gray-200 focus:border-[#c9a962]'
                    } hover:border-[#c9a962]`}
                  />
                </div> */}

                {/* Search Button - Click to search available cars */}
                {/* <div className="sm:col-span-2 lg:col-span-1 flex items-end">
                  <button 
                    type="button" 
                    onClick={handleSearchCars}
                    className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-bold uppercase tracking-wider rounded-lg sm:rounded-xl hover:shadow-2xl hover:shadow-[#c9a962]/40 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 sm:gap-3 text-sm"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search Cars
                  </button>
                </div>
              </div>
            </form> */}

            {/* Quick Features - Trust badges displayed below the form */}
            {/* <div className="relative z-10 flex flex-wrap justify-center gap-3 sm:gap-6 mt-4 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-700/50">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#c9a962]/20 flex items-center justify-center">
                  <span className="text-[10px] sm:text-sm">✓</span>
                </div>
                <span className={`text-[10px] sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Free Cancellation</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#c9a962]/20 flex items-center justify-center">
                  <span className="text-[10px] sm:text-sm">✓</span>
                </div>
                <span className={`text-[10px] sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>No Hidden Charges</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#c9a962]/20 flex items-center justify-center">
                  <span className="text-sm">✓</span>
                </div>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>24/7 Support</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#c9a962]/20 flex items-center justify-center">
                  <span className="text-sm">✓</span>
                </div>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Best Price Guarantee</span>
              </div>
            </div>
          </div>
        </div>
     </div>  */}

      {/* Scroll Indicator - Hidden on mobile */}
      {/* <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 hidden sm:flex flex-col items-center gap-2 animate-bounce">
        <span className={`text-xs uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Scroll Down</span>
        <div className="w-8 h-12 border-2 border-[#c9a962]/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-[#c9a962] rounded-full animate-pulse"></div>
        </div>
      </div> */}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes draw {
          to { stroke-dashoffset: 0; }
        }
        .animate-draw {
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          animation: draw 2s ease forwards;
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease forwards;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 1s ease forwards;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
